import { useEffect, useMemo, useRef, useState } from "react";
import { useViewer } from "../../../state/ViewerProvider";
import type {
  AnimationPresetId,
  AnimationProperty,
  AnimationTarget,
  AnimationTimeline,
  AnimationTrack,
  AnimationPlaybackState,
  ElementInfo,
  GroupInfo,
  MaterialKind,
} from "../../../shared/api-contract";
import { SectionLabel, Select } from "./studioControls";
import { downloadBlob, downloadJSON, pickFile } from "../../../util/download";
import { TimelineTransport } from "./TimelineTransport";
import { TrackList } from "./TrackList";
import { KeyframeEditor } from "./KeyframeEditor";

/** Local MiniButton — ported from src/ui/ring/controls.tsx (which is being deleted). */
function MiniButton({ children, onClick, active }: { children: React.ReactNode; onClick: () => void; active?: boolean }) {
  return (
    <button
      onClick={onClick}
      className={`rounded-lg border px-2.5 py-1.5 text-[12px] font-medium transition-colors ${
        active
          ? "border-ed-text bg-ed-text text-white"
          : "border-ed-line bg-ed-panel text-ed-text hover:border-ed-accent"
      }`}
    >
      {children}
    </button>
  );
}

/** Local NumberBox — ported from src/ui/ring/controls.tsx (which is being deleted). */
function NumberBox({ value, onChange, width = 64 }: { value: string; onChange: (v: string) => void; width?: number }) {
  return (
    <input
      type="number"
      step="0.01"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      style={{ width }}
      className="rounded-md border border-ed-line bg-ed-panel px-1.5 py-1 text-center text-[12px] outline-none focus:border-ed-accent"
    />
  );
}

const TARGET_LABEL: Record<AnimationTarget, string> = {
  model: "Whole Model",
  camera: "Camera",
  group: "Part / Group",
  element: "Single Diamond / Mesh",
};

const PROP_LABEL: Record<AnimationProperty, string> = {
  position: "Move",
  rotation: "Rotate",
  scale: "Scale",
  cameraTarget: "Look-at Target",
  groupOffset: "Move",
  groupRotation: "Rotate",
  groupScale: "Scale",
  visibility: "Visibility",
  gemColor: "Colour",
  gemBoost: "Brilliance",
  gemOpacity: "Opacity",
  metalColor: "Colour",
  metalRoughness: "Roughness",
  metalMetalness: "Metalness",
};

/** Material properties available for each material kind. */
function materialProps(kind: MaterialKind | undefined): AnimationProperty[] {
  if (kind === "gem") return ["gemColor", "gemBoost", "gemOpacity"];
  if (kind === "metal") return ["metalColor", "metalRoughness", "metalMetalness"];
  return [];
}

/** Property choices for the current target + (for group/element) material kind. */
function propsFor(target: AnimationTarget, kind: MaterialKind | undefined): AnimationProperty[] {
  if (target === "model") return ["position", "rotation", "scale"];
  if (target === "camera") return ["position", "cameraTarget"];
  if (target === "group") return ["groupOffset", "groupRotation", "groupScale", "visibility", ...materialProps(kind)];
  // element
  return ["position", "rotation", "scale", "visibility", ...materialProps(kind)];
}

const PRESET_HINT: Record<string, string> = {
  orbit360: "Spins the model a full turn — great for 360° product videos.",
  ringFall: "Drops the ring in with a bounce.",
  boxReveal: "Reveals the ring rising out of the box.",
  ringSplit: "Separates the metal parts, then reunites them.",
  diamondMove: "Lifts and settles the gemstones.",
  diamondRain: "Ring spins while each diamond drops from the sky 0.10s apart and settles in.",
};

function neutralValue(p: AnimationProperty): number[] {
  if (p === "visibility") return [1];
  if (p === "scale" || p === "groupScale") return [1, 1, 1];
  if (p === "gemColor" || p === "metalColor" || p === "gemBoost") return [1, 1, 1];
  if (p === "gemOpacity" || p === "metalMetalness") return [1];
  if (p === "metalRoughness") return [0.3];
  return [0, 0, 0];
}

function hexToRgb(hex: string): number[] {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return [1, 1, 1];
  const n = parseInt(m[1], 16);
  return [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255];
}

/** Assign a delay slot to each element index per ordering strategy.
 * Returns slotOf[elementIndex] = slot (0-based delay multiplier). */
function staggerSlots(n: number, order: string): number[] {
  const idx = Array.from({ length: n }, (_, i) => i);
  if (order === "center-out") {
    const c = (n - 1) / 2;
    idx.sort((a, b) => Math.abs(a - c) - Math.abs(b - c));
  } else if (order === "random") {
    for (let i = n - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [idx[i], idx[j]] = [idx[j], idx[i]];
    }
  }
  const slotOf = new Array<number>(n);
  idx.forEach((elemIdx, slot) => { slotOf[elemIdx] = slot; });
  return slotOf;
}

interface StaggerOpts {
  delay: number;
  order: string;
  template: "fall" | "color";
  height: number;
  fromColor: string;
  toColor: string;
}

/** Build time-shifted per-element tracks (host-side) for the Stagger tool. */
function buildStaggerTracks(elements: ElementInfo[], opts: StaggerOpts): { tracks: AnimationTrack[]; duration: number } {
  const slotOf = staggerSlots(elements.length, opts.order);
  const tracks: AnimationTrack[] = [];
  const span = opts.template === "fall" ? 0.9 : 0.5;
  let maxOffset = 0;
  for (let i = 0; i < elements.length; i++) maxOffset = Math.max(maxOffset, slotOf[i] * opts.delay);
  const duration = maxOffset + span + 0.6;
  const from = hexToRgb(opts.fromColor);
  const to = hexToRgb(opts.toColor);

  elements.forEach((el, i) => {
    const offset = slotOf[i] * opts.delay;
    if (opts.template === "fall") {
      tracks.push({
        id: `stagger-pos-${el.id}`,
        target: "element",
        property: "position",
        targetId: el.id,
        enabled: true,
        keyframes: [
          { time: 0, value: [0, opts.height, 0], easing: "easeIn" },
          { time: offset, value: [0, opts.height, 0], easing: "easeIn" },
          { time: offset + span, value: [0, 0, 0], easing: "easeOut" },
          { time: duration, value: [0, 0, 0], easing: "linear" },
        ],
      });
      if (el.kind === "gem") {
        tracks.push({
          id: `stagger-op-${el.id}`,
          target: "element",
          property: "gemOpacity",
          targetId: el.id,
          enabled: true,
          keyframes: [
            { time: 0, value: [0], easing: "linear" },
            { time: offset, value: [0], easing: "linear" },
            { time: offset + 0.12, value: [1], easing: "easeOut" },
            { time: duration, value: [1], easing: "linear" },
          ],
        });
      }
    } else {
      tracks.push({
        id: `stagger-col-${el.id}`,
        target: "element",
        property: el.kind === "gem" ? "gemColor" : "metalColor",
        targetId: el.id,
        enabled: true,
        keyframes: [
          { time: 0, value: [...from], easing: "linear" },
          { time: offset, value: [...from], easing: "linear" },
          { time: offset + span, value: [...to], easing: "easeInOut" },
          { time: duration, value: [...to], easing: "linear" },
        ],
      });
    }
  });

  return { tracks, duration };
}

function emptyTimeline(): AnimationTimeline {
  return { name: "Custom Timeline", duration: 4, tracks: [] };
}

export function AnimationPanel() {
  const { client } = useViewer();
  const r = client.ring;

  const [hasModel, setHasModel] = useState(true);
  const [timeline, setTimeline] = useState<AnimationTimeline>(emptyTimeline());
  const [state, setState] = useState<AnimationPlaybackState>({
    playing: false, currentTime: 0, duration: 4, loop: false, playbackRate: 1, timelineId: null,
  });
  const [selectedTrackId, setSelectedTrackId] = useState<string | null>(null);
  const [groups, setGroups] = useState<GroupInfo[]>([]);
  const [elements, setElements] = useState<ElementInfo[]>([]);
  const [presets, setPresets] = useState<Array<{ id: string; label: string }>>([]);
  const [target, setTarget] = useState<AnimationTarget>("model");
  const [property, setProperty] = useState<AnimationProperty>("position");
  const [targetId, setTargetId] = useState<string>("");
  const [activePreset, setActivePreset] = useState<string | null>(null);
  // Stagger tool
  const [staggerGroup, setStaggerGroup] = useState<string>("");
  const [staggerDelay, setStaggerDelay] = useState("0.10");
  const [staggerOrder, setStaggerOrder] = useState("in-order");
  const [staggerTemplate, setStaggerTemplate] = useState<"fall" | "color">("fall");
  const [staggerHeight, setStaggerHeight] = useState("25");
  const [staggerFrom, setStaggerFrom] = useState("#ffffff");
  const [staggerTo, setStaggerTo] = useState("#ff5fa2");
  const [recordW, setRecordW] = useState("1600");
  const [recordH, setRecordH] = useState("1600");
  const [recordFps, setRecordFps] = useState("30");
  const [recording, setRecording] = useState(false);
  const [busy, setBusy] = useState(false);

  const scrubbing = useRef(false);

  const refreshModelState = async () => {
    const [models, gs, els] = await Promise.all([r.listModels(), r.listGroups(), r.listElements()]);
    setHasModel(models.length > 0);
    setGroups(gs);
    setElements(els);
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      const [ps, models, gs, els, tl, st] = await Promise.all([
        r.listAnimationPresets(), r.listModels(), r.listGroups(), r.listElements(), r.getAnimationTimeline(), r.getAnimationState(),
      ]);
      if (!alive) return;
      setPresets(ps);
      setHasModel(models.length > 0);
      setGroups(gs);
      setElements(els);
      if (tl) {
        setTimeline(tl);
        setState(st);
      } else if (models.length > 0) {
        // Register the empty timeline so duration/transport stay coherent even
        // before a preset is applied.
        const fresh = emptyTimeline();
        setTimeline(fresh);
        setState(await r.setAnimationTimeline(fresh));
      } else {
        setState(st);
      }
    })();

    const offModels = client.on("modelsChanged", () => { void refreshModelState(); });

    const id = setInterval(async () => {
      if (!alive || scrubbing.current) return;
      const st = await r.getAnimationState();
      if (!alive) return;
      setState(st);
    }, 200);

    return () => { alive = false; clearInterval(id); offModels(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, r]);

  const selectedTrack = useMemo(
    () => timeline.tracks.find((t) => t.id === selectedTrackId) ?? null,
    [timeline, selectedTrackId],
  );

  const labelOf = (t: AnimationTrack): string => {
    const prop = PROP_LABEL[t.property];
    if (t.target === "group") {
      const name = t.targetId === "__box__" ? "Ring Box" : groups.find((g) => g.id === t.targetId)?.label ?? "Part";
      return `${name} · ${prop}`;
    }
    if (t.target === "element") {
      const name = elements.find((e) => e.id === t.targetId)?.label ?? "Element";
      return `${name} · ${prop}`;
    }
    return `${TARGET_LABEL[t.target]} · ${prop}`;
  };

  /** Material kind of the currently picked group/element (for property menu). */
  const currentKind = (): MaterialKind | undefined => {
    if (target === "group") return groups.find((g) => g.id === (targetId || groups[0]?.id))?.kind;
    if (target === "element") return elements.find((e) => e.id === (targetId || elements[0]?.id))?.kind;
    return undefined;
  };
  const availableProps = propsFor(target, currentKind());

  const syncTimeline = async (next: AnimationTimeline) => {
    setTimeline(next);
    const st = await r.setAnimationTimeline(next);
    setState(st);
  };

  const applyPreset = async (id: string) => {
    setBusy(true);
    try {
      const tl = await r.applyAnimationPreset(id as AnimationPresetId);
      setTimeline(tl);
      setActivePreset(id);
      setSelectedTrackId(tl.tracks[0]?.id ?? null);
      setState(await r.getAnimationState());
    } finally {
      setBusy(false);
    }
  };

  const onTargetChange = (next: AnimationTarget) => {
    setTarget(next);
    const tid = next === "group" ? groups[0]?.id ?? "" : next === "element" ? elements[0]?.id ?? "" : "";
    setTargetId(tid);
    const kind = next === "group" ? groups[0]?.kind : next === "element" ? elements[0]?.kind : undefined;
    setProperty(propsFor(next, kind)[0]);
  };

  const onTargetIdChange = (id: string) => {
    setTargetId(id);
    const kind = target === "group" ? groups.find((g) => g.id === id)?.kind : elements.find((e) => e.id === id)?.kind;
    const props = propsFor(target, kind);
    if (!props.includes(property)) setProperty(props[0]);
  };

  const addTrack = async () => {
    const id = `track_${Date.now().toString(36)}`;
    const tid = target === "group" || target === "element" ? (targetId || (target === "group" ? groups[0]?.id : elements[0]?.id)) : undefined;
    const track: AnimationTrack = {
      id, target, property, targetId: tid, enabled: true,
      keyframes: [
        { time: 0, value: neutralValue(property), easing: "linear" },
        { time: Math.max(0.5, timeline.duration), value: neutralValue(property), easing: "linear" },
      ],
    };
    const next = { ...timeline, tracks: [...timeline.tracks, track] };
    setActivePreset(null);
    await syncTimeline(next);
    setSelectedTrackId(id);
  };

  const updateTrack = async (track: AnimationTrack) => {
    setActivePreset(null);
    await syncTimeline({ ...timeline, tracks: timeline.tracks.map((t) => (t.id === track.id ? track : t)) });
  };

  const removeTrack = async (id: string) => {
    await syncTimeline({ ...timeline, tracks: timeline.tracks.filter((t) => t.id !== id) });
    if (selectedTrackId === id) setSelectedTrackId(null);
  };

  const clearAll = async () => {
    setActivePreset(null);
    setSelectedTrackId(null);
    await r.clearAnimationTimeline();
    const fresh = emptyTimeline();
    setTimeline(fresh);
    setState(await r.getAnimationState());
  };

  const gemGroups = useMemo(() => groups.filter((g) => g.kind === "gem"), [groups]);

  const generateStagger = async () => {
    const gid = staggerGroup || gemGroups[0]?.id || groups[0]?.id;
    if (!gid) return;
    setBusy(true);
    try {
      const els = await r.listElements(gid);
      if (!els.length) return;
      const { tracks, duration } = buildStaggerTracks(els, {
        delay: parseFloat(staggerDelay) || 0.1,
        order: staggerOrder,
        template: staggerTemplate,
        height: parseFloat(staggerHeight) || 20,
        fromColor: staggerFrom,
        toColor: staggerTo,
      });
      // The stagger tool owns every "stagger-" track; replace them wholesale so
      // re-generating doesn't pile up duplicates.
      const kept = timeline.tracks.filter((t) => !t.id.startsWith("stagger-"));
      const next = { ...timeline, duration: Math.max(timeline.duration, duration), tracks: [...kept, ...tracks] };
      setActivePreset(null);
      await syncTimeline(next);
      setSelectedTrackId(tracks[0]?.id ?? null);
      void r.playAnimation().then(setState);
    } finally {
      setBusy(false);
    }
  };

  const recordOpts = () => ({
    width: parseInt(recordW) || 1600,
    height: parseInt(recordH) || 1600,
    fps: parseInt(recordFps) || 30,
  });

  const resyncAfterRecord = async () => {
    const tl = await r.getAnimationTimeline();
    if (tl) setTimeline(tl);
    setState(await r.getAnimationState());
  };

  const export360 = async () => {
    setRecording(true);
    try {
      const blob = await r.record360Video({ ...recordOpts(), duration: 6 });
      downloadBlob(blob, "ring-360.webm");
    } finally {
      setRecording(false);
      await resyncAfterRecord();
    }
  };

  const exportTimeline = async () => {
    setRecording(true);
    try {
      const blob = await r.exportAnimationVideo(timeline, recordOpts());
      downloadBlob(blob, "ring-animation.webm");
    } finally {
      setRecording(false);
      await resyncAfterRecord();
    }
  };

  if (!hasModel) {
    return (
      <div className="flex flex-col gap-2">
        <SectionLabel>Animation</SectionLabel>
        <span className="text-[12px] leading-relaxed text-ed-text-2">
          Load a model first (Models tab). Animation presets, the timeline builder and video
          export all work on the currently loaded model.
        </span>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <SectionLabel>Presets</SectionLabel>
      <span className="text-[12px] leading-snug text-ed-text-2">
        One click applies and plays the effect. Use the transport below to replay or scrub.
      </span>
      <div className="flex flex-wrap gap-1.5">
        {presets.map((p) => (
          <MiniButton key={p.id} active={activePreset === p.id} onClick={() => void applyPreset(p.id)}>
            {p.label}
          </MiniButton>
        ))}
      </div>
      {activePreset && PRESET_HINT[activePreset] ? (
        <span className="text-[12px] leading-snug text-ed-text-2">{PRESET_HINT[activePreset]}</span>
      ) : null}

      <SectionLabel>Playback</SectionLabel>
      <TimelineTransport
        state={state}
        disabled={busy || recording}
        onPlay={() => void r.playAnimation().then(setState)}
        onPause={() => void r.pauseAnimation().then(setState)}
        onStop={() => void r.stopAnimation().then(setState)}
        onSeek={(t) => void r.seekAnimation(t).then((s) => { if (scrubbing.current) setState(s); })}
        onScrubStart={() => { scrubbing.current = true; }}
        onScrubEnd={() => { scrubbing.current = false; }}
        onToggleLoop={(v) => void r.setAnimationLoop(v).then(setState)}
        onRate={(v) => void r.setAnimationPlaybackRate(v).then(setState)}
      />
      <div className="flex items-center gap-2">
        <span className="text-[12px] font-medium text-ed-text-2">Duration</span>
        <NumberBox
          value={timeline.duration.toFixed(2)}
          width={64}
          onChange={(v) => void syncTimeline({ ...timeline, duration: Math.max(0.1, parseFloat(v) || 0.1) })}
        />
        <span className="text-[11px] text-ed-text-2">seconds</span>
      </div>

      <SectionLabel>Tracks</SectionLabel>
      <TrackList
        tracks={timeline.tracks}
        duration={timeline.duration}
        selectedTrackId={selectedTrackId}
        labelOf={labelOf}
        onSelectTrack={setSelectedTrackId}
        onRemoveTrack={(id) => void removeTrack(id)}
      />

      <div className="flex flex-col gap-1.5 rounded-lg bg-ed-panel-2 p-2">
        <span className="text-[12px] font-medium text-ed-text-2">Add a track</span>
        <div className="flex flex-wrap items-end gap-1.5">
          <div className="w-[150px]">
            <Select
              variant="boxed"
              value={target}
              options={(Object.keys(TARGET_LABEL) as AnimationTarget[]).map((t) => ({ id: t, label: TARGET_LABEL[t] }))}
              onChange={(v) => onTargetChange(v as AnimationTarget)}
            />
          </div>
          {target === "group" ? (
            <div className="w-[150px]">
              <Select
                variant="boxed"
                value={targetId || groups[0]?.id || ""}
                options={groups.map((g) => ({ id: g.id, label: `${g.label} (${g.kind})` }))}
                onChange={onTargetIdChange}
              />
            </div>
          ) : null}
          {target === "element" ? (
            <div className="w-[170px]">
              <Select
                variant="boxed"
                value={targetId || elements[0]?.id || ""}
                options={elements.map((e) => ({ id: e.id, label: e.label }))}
                onChange={onTargetIdChange}
              />
            </div>
          ) : null}
          <div className="w-[120px]">
            <Select
              variant="boxed"
              value={property}
              options={availableProps.map((p) => ({ id: p, label: PROP_LABEL[p] }))}
              onChange={(v) => setProperty(v as AnimationProperty)}
            />
          </div>
          <MiniButton onClick={() => void addTrack()}>Add</MiniButton>
        </div>
        {target === "element" && elements.length === 0 ? (
          <span className="text-[11px] text-ed-text-2">No individual meshes detected on this model.</span>
        ) : null}
      </div>

      <SectionLabel>Stagger Tool (Diamond-by-Diamond)</SectionLabel>
      <span className="text-[12px] leading-snug text-ed-text-2">
        Auto-generate one track per diamond in a group, each delayed from the last — the “0.10s apart” effect in one click.
      </span>
      <div className="flex flex-col gap-1.5 rounded-lg bg-ed-panel-2 p-2">
        <div className="flex flex-wrap items-end gap-1.5">
          <div className="w-[150px]">
            <Select
              variant="boxed"
              value={staggerGroup || gemGroups[0]?.id || groups[0]?.id || ""}
              options={(gemGroups.length ? gemGroups : groups).map((g) => ({ id: g.id, label: `${g.label} (${g.kind})` }))}
              onChange={setStaggerGroup}
            />
          </div>
          <div className="w-[130px]">
            <Select
              variant="boxed"
              value={staggerTemplate}
              options={[{ id: "fall", label: "Fall from sky" }, { id: "color", label: "Colour wave" }]}
              onChange={(v) => setStaggerTemplate(v as "fall" | "color")}
            />
          </div>
          <div className="w-[130px]">
            <Select
              variant="boxed"
              value={staggerOrder}
              options={[{ id: "in-order", label: "In order" }, { id: "center-out", label: "Center-out" }, { id: "random", label: "Random" }]}
              onChange={setStaggerOrder}
            />
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[12px] font-medium text-ed-text-2">Delay</span>
          <NumberBox value={staggerDelay} width={56} onChange={setStaggerDelay} />
          <span className="text-[11px] text-ed-text-2">sec</span>
          {staggerTemplate === "fall" ? (
            <>
              <span className="text-[12px] font-medium text-ed-text-2">Drop height</span>
              <NumberBox value={staggerHeight} width={56} onChange={setStaggerHeight} />
            </>
          ) : (
            <>
              <span className="text-[12px] font-medium text-ed-text-2">From</span>
              <input type="color" value={staggerFrom} onChange={(e) => setStaggerFrom(e.target.value)}
                className="h-7 w-9 cursor-pointer rounded-md border border-ed-line bg-transparent p-0.5" aria-label="From colour" />
              <span className="text-[12px] font-medium text-ed-text-2">To</span>
              <input type="color" value={staggerTo} onChange={(e) => setStaggerTo(e.target.value)}
                className="h-7 w-9 cursor-pointer rounded-md border border-ed-line bg-transparent p-0.5" aria-label="To colour" />
            </>
          )}
          <MiniButton onClick={() => void generateStagger()}>Generate</MiniButton>
        </div>
      </div>

      <SectionLabel>Keyframes</SectionLabel>
      <KeyframeEditor track={selectedTrack} onUpdate={(t) => void updateTrack(t)} />

      <div className="flex flex-wrap gap-2">
        <MiniButton onClick={() => downloadJSON(JSON.stringify(timeline, null, 2), "animation.json")}>Export JSON</MiniButton>
        <MiniButton onClick={() => pickFile(".json", async (f) => {
          try {
            const tl = JSON.parse(await f.text()) as AnimationTimeline;
            setActivePreset(null);
            await syncTimeline(tl);
            setSelectedTrackId(tl.tracks?.[0]?.id ?? null);
          } catch { /* ignore malformed json */ }
        })}>Import JSON</MiniButton>
        <MiniButton onClick={() => void clearAll()}>Clear</MiniButton>
      </div>

      <SectionLabel>Export Video (WebM)</SectionLabel>
      <div className="flex items-center gap-2">
        <NumberBox value={recordW} width={60} onChange={setRecordW} />
        <span className="text-ed-text-2">×</span>
        <NumberBox value={recordH} width={60} onChange={setRecordH} />
        <span className="text-[12px] font-medium text-ed-text-2">FPS</span>
        <NumberBox value={recordFps} width={48} onChange={setRecordFps} />
      </div>
      <div className="flex flex-wrap gap-2">
        <MiniButton onClick={() => void export360()}>{recording ? "Rendering…" : "360° Video"}</MiniButton>
        <MiniButton onClick={() => void exportTimeline()}>
          {recording ? "Rendering…" : "Current Timeline"}
        </MiniButton>
      </div>
      {recording ? (
        <span className="text-[12px] text-ed-text-2">
          Rendering frames — keep this panel open until the download starts.
        </span>
      ) : null}
    </div>
  );
}
